import {Component} from '@angular/core';
import {AbstractUnits} from '../abstract-units';
import {SolarSubTechnology, solarSubTechnologyToLabel} from '../../../model/technical/SolarSubTechnology';
import {SolarFacialType, solarFacialTypeToLabel} from '../../../model/technical/SolarFacialType';
import {SolarModuleType, solarModuleTypeToLabel} from '../../../model/technical/SolarModuleType';
import {SolarMountingSystem, solarMountingSystemToLabel} from '../../../model/technical/SolarMountingSystem';
import {UnitTypeEntry} from '../UnitTypeEntry';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {UnitTypeSolar} from '../../../model/technical/UnitTypeSolar';
import {ProjectDetailsTab} from '../../../../project-details/ProjectDetailsTab';
import {CalcUtil} from '../../../CalcUtil';
import {ReadOnlyReason, ReadOnlyReasonToTooltip} from '../../../../project-details/ReadOnlyStateManager';

@Component({
  selector: 'digires-units-solar',
  templateUrl: './units-solar.component.html',
  styleUrls: ['./units-solar.component.scss']
})
export class UnitsSolarComponent extends AbstractUnits {

  public static createDefaultUnits(
    projectCreation: boolean,
    unitA?: UnitTypeSolar,
    unitB?: UnitTypeSolar,
    unitC?: UnitTypeSolar,
    unitD?: UnitTypeSolar,
  ): UnitTypeEntry[] {
    return UnitsSolarComponent.createDefaultUnitsInternal(
      projectCreation,
      unitA || UnitsSolarComponent.createDefaultUnit('solarA'),
      unitB || UnitsSolarComponent.createDefaultUnit('solarB'),
      unitC || UnitsSolarComponent.createDefaultUnit('solarC'),
      unitD || UnitsSolarComponent.createDefaultUnit('solarD'),
    );
  }

  private static createDefaultUnitsInternal(
    projectCreation: boolean,
    unitA: UnitTypeSolar,
    unitB: UnitTypeSolar,
    unitC: UnitTypeSolar,
    unitD: UnitTypeSolar,
  ): UnitTypeEntry[] {
    return [
      {
        id: 'solarUnitA',
        active: true,
        canAdd: false,
        canDelete: false,
        label: 'Solar Module Type A',
        fg: UnitsSolarComponent.createUnitFg(unitA, projectCreation),
      },
      {
        id: 'solarUnitB',
        active: unitB.active,
        canAdd: false,
        canDelete: false,
        label: 'Solar Module Type B',
        fg: UnitsSolarComponent.createUnitFg(unitB, projectCreation),
      },
      {
        id: 'solarUnitC',
        active: unitC.active,
        canAdd: false,
        canDelete: false,
        label: 'Solar Module Type C',
        fg: UnitsSolarComponent.createUnitFg(unitC, projectCreation),
      },
      {
        id: 'solarUnitD',
        active: unitD.active,
        canAdd: false,
        canDelete: false,
        label: 'Solar Module Type D',
        fg: UnitsSolarComponent.createUnitFg(unitD, projectCreation),
      },
    ];
  }

  private static createUnitFg(unit: UnitTypeSolar, projectCreation: boolean): FormGroup {
    const validatorsSubTech = projectCreation ? [Validators.required] : [];
    return new FormGroup({
      subTechnology: new FormControl(unit.subTechnology, validatorsSubTech),
      manufacturer: new FormControl(unit.manufacturer),
      moduleType: new FormControl(unit.moduleType),
      count: new FormControl(unit.count),
      capacity: new FormControl(unit.capacity),
      totalCapacity: new FormControl(CalcUtil.calculateTotalCapacity(unit.count, unit.capacity, 0.000001)),
      areaSize: new FormControl(unit.areaSize),
      description: new FormControl(unit.description),
      facialType: new FormControl(unit.facialType),
      mountingSystem: new FormControl(unit.mountingSystem),
      inverterModel: new FormControl(unit.inverterModel),
      inverterCount: new FormControl(unit.inverterCount),
      inverterType: new FormControl(unit.inverterType),
    });
  }

  private static createDefaultUnit(id: string): UnitTypeSolar {
    return {
      id: id,
      active: false,
      subTechnology: null,
      manufacturer: null,
      moduleType: null,
      count: null,
      capacity: null,
      areaSize: null,
      description: null,
      facialType: null,
      mountingSystem: null,
      inverterModel: null,
      inverterCount: null,
      inverterType: null,
    };
  }

  readonly allSubTechnologies = Object.values(SolarSubTechnology);
  readonly allFacialTypes = Object.values(SolarFacialType);
  readonly allModuleTypes = Object.values(SolarModuleType);
  readonly allMountingSystems = Object.values(SolarMountingSystem);
  readonly calculatedReadOnlyReason = ReadOnlyReasonToTooltip[ReadOnlyReason.CALCULATED];

  constructor() {super(ProjectDetailsTab.solar, 0.000001);}

  subTechnologyToLabel(value: SolarSubTechnology): string {
    return solarSubTechnologyToLabel[value];
  }

  facialTypeToLabel(value: SolarFacialType): string {
    return solarFacialTypeToLabel[value];
  }

  moduleTypeToLabel(value: SolarModuleType): string {
    return solarModuleTypeToLabel[value];
  }

  mountingSystemToLabel(value: SolarMountingSystem): string {
    return solarMountingSystemToLabel[value];
  }

}
